import { Store } from '@reduxjs/toolkit';
import { createInstance } from '@optimizely/react-sdk';
import { updateOptimizelyClientStatus } from '../store/slices/onboardingSlice';
import { Logger } from './logging/logger';
// Written by `scripts/fetchOptimizelyDatafile.mjs` before start/build/test.
import fallbackDatafile from '../generated/optimizelyDatafile.json';

const logger = Logger.create('optimizely');

/**
 * How long the SDK is given to become ready before decisions fall back to their
 * defaults. The react-sdk default is 5s, which is too tight for slow mobile
 * networks and made flags silently degrade to "off". Applied by
 * `AppOptimizelyProvider`, which is how the provider should always be mounted.
 */
export const optimizelyReadyTimeoutMs = 10000;

/**
 * How often the datafile is refreshed once loaded, so a long-lived session picks
 * up flag changes instead of holding the datafile it booted with.
 */
const datafileUpdateIntervalMs = 5 * 60 * 1000;

/**
 * How long before a datafile download that hasn't finished marks the client
 * status `error`, which lets `useOptimizelyFlag` decide from the datafile saved
 * at build time. A download that fails outright (e.g. blocked by a VPN) reports
 * through `errorHandler` straight away. This is the react-sdk default, kept
 * shorter than `optimizelyReadyTimeoutMs` so a stalled download doesn't hold
 * the loader for the full provider timeout.
 */
const clientStatusTimeoutMs = 5000;

/**
 * Where the datafile is downloaded from. On deployed environments it is served
 * from our own host through an Amplify rewrite to `cdn.optimizely.com` (see
 * `OptimusWebappStack`), because VPN / DNS ad-blockers (e.g. NordVPN Threat
 * Protection) block the Optimizely CDN, which turned every flag off (SN-1027).
 * Locally there is no such rewrite, so the SDK default CDN is used.
 */
const datafileUrlTemplate = process.env.REACT_APP_OPTIMIZELY_DATAFILE_PROXY === 'true' ?
  `${window.location.origin}/optimizely/datafiles/%s.json` :
  undefined;

/**
 * Initializes Optimizely
 */
const initialize = (store: Store) => {
  const optimizelyErrorHandler = {
    /**
     * handleError
     *
     * Function which gets called when an error is thrown in the SDK
     * @param {Object} error - error object
     * @param {String} error.message - message of the error
     * TODO: sdk does not consider wrong sdk key as an error. Need to handle this case
     */
    handleError: (error: Error) => {
      logger.error(Logger.format(`optimizely:Error Message: ${error.message}`));
      // update optimizely client valid state
      store.dispatch(updateOptimizelyClientStatus('error'));
    },
  };

  const optimizelyClient = createInstance({
    sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
    // Flag settings saved at build time: if the live datafile can't be
    // downloaded, flags are decided from this copy instead of all being off.
    datafile: fallbackDatafile ?? undefined,
    errorHandler: optimizelyErrorHandler,
    datafileOptions: {
      autoUpdate: true,
      updateInterval: datafileUpdateIntervalMs,
      urlTemplate: datafileUrlTemplate,
    },
  });

  store.dispatch(updateOptimizelyClientStatus('pending'));
  optimizelyClient?.onReady({ timeout: clientStatusTimeoutMs }).then((result) => {
    logger.debug(Logger.format('optimizely:onReady', result));
    if (result.success) {
      store.dispatch(updateOptimizelyClientStatus('success'));
    } else {
      store.dispatch(updateOptimizelyClientStatus('error'));
    }
  });

  return optimizelyClient;
};

/**
 * Resets the Optimizely user back to anonymous, so flags stop being evaluated
 * against the member who just logged out.
 *
 * The client is a singleton that holds the last user it was given. Pre-auth
 * screens mount the provider without a `user` prop, and in that case the SDK
 * keeps the existing `optimizely.user` rather than falling back to the
 * anonymous default — so without this the next person to reach the login screen
 * would be decided as the previous member until they signed in.
 */
const clearUser = () => {
  try {
    if (!global.optimizely) {
      return;
    }
    logger.debug(Logger.format('optimizely:clearing user'));
    global.optimizely.setUser({ id: null });
  } catch (e) {
    logger.error(Logger.format('optimizely:Error clearing user', e));
  }
};

/**
 * Object for manipulating Heap Analytics.
 */
export const ReactOptimizely = {
  initialize,
  clearUser,
};
